import { onMounted, onBeforeUnmount, computed, watch, nextTick, getCurrentInstance } from 'vue';
import { EventBus } from '@/assets/js/event-bus';
import { isTargetInside, targetHasClass } from '../utils/domUtils';

export interface AnnotationFocusData {
  annotationId: string;
  isVertical: boolean;
  isNew: boolean;
}

export interface MenuCheckEvent {
  event: Event;
  callback: (result: boolean) => void;
}

export interface MenuTargetPositionEvent {
  event: MouseEvent;
  menuEl: HTMLElement;
}

type StoreLike = {
  state: any;
  commit: (type: string, payload?: any) => void;
};

export function useDomInteractions(opts?: { store?: StoreLike }) {
  // Vuex store: prefer explicit param; fallback to current instance.$store (Vue 2.x)
  const inst = getCurrentInstance();
  const store: StoreLike | undefined = opts?.store ?? (inst?.proxy as any)?.$store;

  if (!store) {
    throw new Error(
      'useDomInteractions requires a Vuex store (pass opts.store or ensure this.$store is available).'
    );
  }

  function focusAnnotation({ annotationId, isVertical, isNew }: AnnotationFocusData): void {
    const selector = isVertical
      ? `#vertical-annotation-${annotationId} .ProseMirror`
      : `#annotation-${annotationId} .ProseMirror`;

    const element = document.querySelector(selector) as HTMLElement | null;
    if (!element) return;

    if (isVertical) {
      setTimeout(() => element.focus(), 800);
    } else if (isNew) {
      element.focus();
    }
  }

  function clearSelection(): void {
    window.getSelection?.()?.empty?.();
  }

  function getScrollerTop(): number {
    const scroller = document.getElementById('counterpoint-scroller');
    return scroller ? scroller.scrollTop : 0;
  }

  function updateScrollerOffset(): void {
    const offset = getScrollerTop();
    store?.commit('setScrollerOffset', offset);
  }

  function positionVerticalMenu(event: MouseEvent, menuEl: HTMLElement): void {
    const verticalModalBody = document.querySelector('#vertical-modal-body') as HTMLElement | null;
    if (!verticalModalBody) return;

    const menu = verticalModalBody.querySelector('#annotations-menu') as HTMLElement | null;
    if (!menu) return;

    const target = event.target as HTMLElement;
    const top =
      target.getBoundingClientRect().top -
      verticalModalBody.getBoundingClientRect().top -
      menuEl.offsetHeight;
    const left = event.offsetX + menuEl.offsetWidth;

    menu.style.top = `${top}px`;
    menu.style.left = `${left}px`;
  }

  function handleMenuResetCheck({ event, callback }: MenuCheckEvent): void {
    const shouldReset =
      !isTargetInside(event, '#currentMainPanel #annotatorArticle') &&
      !isTargetInside(event, '#currentMainPanel #outline-tree') &&
      !isTargetInside(event, '.annotation');
    callback(shouldReset);
  }

  function handleMenuOptionCheck({ event, callback }: MenuCheckEvent): void {
    const isMenuOption = isTargetInside(event, '#annotations-menu');
    callback(isMenuOption);
  }

  function handleMenuTargetAndPosition({ event, menuEl }: MenuTargetPositionEvent): void {
    if (!targetHasClass(event, 'citation-img')) {
      positionVerticalMenu(event, menuEl);
    }
  }

  const pendingAnnotationFocus = computed<AnnotationFocusData | null>(
    () => store?.state.pendingAnnotationFocus ?? null
  );

  const shouldClearSelection = computed<boolean>(() => !!store?.state.shouldClearSelection);

  watch(pendingAnnotationFocus, focusData => {
    if (!focusData) return;
    nextTick(() => {
      focusAnnotation(focusData);
      store?.commit('setPendingAnnotationFocus', null);
    });
  });

  watch(shouldClearSelection, shouldClear => {
    if (!shouldClear) return;
    clearSelection();
    store?.commit('setShouldClearSelection', false);
  });

  onMounted(() => {
    EventBus.on('menu.checkShouldReset', handleMenuResetCheck);
    EventBus.on('menu.checkIsMenuOption', handleMenuOptionCheck);
    EventBus.on('menu.checkTargetAndPosition', handleMenuTargetAndPosition);
  });

  onBeforeUnmount(() => {
    EventBus.off('menu.checkShouldReset', handleMenuResetCheck);
    EventBus.off('menu.checkIsMenuOption', handleMenuOptionCheck);
    EventBus.off('menu.checkTargetAndPosition', handleMenuTargetAndPosition);
  });

  return {
    // state
    pendingAnnotationFocus,
    shouldClearSelection,
    // methods
    focusAnnotation,
    clearSelection,
    getScrollerTop,
    updateScrollerOffset,
    positionVerticalMenu,
    // exposing for tests/manual usage
    handleMenuResetCheck,
    handleMenuOptionCheck,
    handleMenuTargetAndPosition,
  };
}
